import { useEffect, useState, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { NSWordmark, SwarmMoon, Icon, I, MonoChip } from "../kit";
import "../docs.css";

/**
 * Docs — the public documentation page. One long page, sticky in-page
 * nav, same page language as the marketing page (kickers, section
 * titles, mono labels, cards). Every section leads with plain English,
 * then folds the precise mechanics into a collapsible "under the hood"
 * layer for advanced users. Facts verified against docs/ contracts
 * (steel-thread, phase 2/3/4/6/8/11) and the shipped daemon/menubar/app.
 */

const SECTIONS = [
  { id: "concepts", n: "01", t: "Concepts" },
  { id: "getting-started", n: "02", t: "Getting started" },
  { id: "agents", n: "03", t: "Configuring agents" },
  { id: "talking", n: "04", t: "Talking to agents" },
  { id: "dispatch", n: "05", t: "Dispatch profiles" },
  { id: "schedules", n: "06", t: "Schedules" },
  { id: "needs-you", n: "07", t: "When an agent needs you" },
  { id: "teams", n: "08", t: "Teams" },
  { id: "pr-reviews", n: "09", t: "PR reviews" },
  { id: "workflows", n: "10", t: "Common workflows" },
  { id: "best-practices", n: "11", t: "Best practices" },
  { id: "troubleshooting", n: "12", t: "Troubleshooting" },
] as const;

/** Scroll-spy: highlight the TOC entry for the section in view. */
function useActiveSection(ids: readonly string[]) {
  const [active, setActive] = useState<string>(ids[0]);
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        for (const e of entries) {
          if (e.isIntersecting) setActive(e.target.id);
        }
      },
      { rootMargin: "-15% 0px -75% 0px" }
    );
    for (const id of ids) {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    }
    return () => observer.disconnect();
  }, [ids]);
  return active;
}

/* ── Page scaffolding ────────────────────────────────────────────── */

function DocTopbar() {
  return (
    <div className="doc-topbar">
      <div className="doc-container doc-topbar-inner">
        <Link to="/" className="doc-topbar-home" aria-label="nightswarm.app home">
          <NSWordmark size={17} />
        </Link>
        <MonoChip tone="ghost">docs</MonoChip>
        <div className="doc-topbar-right">
          <Link to="/" className="doc-toplink">
            Home
          </Link>
          <Link to="/dashboard" className="ns-btn ns-btn--primary ns-btn--sm">
            Open dashboard
          </Link>
        </div>
      </div>
    </div>
  );
}

function DocToc({ active }: { active: string }) {
  return (
    <aside className="doc-toc">
      <div className="doc-toc-h">On this page</div>
      <nav aria-label="Documentation sections">
        {SECTIONS.map((s) => (
          <a
            key={s.id}
            href={`#${s.id}`}
            className={active === s.id ? "active" : undefined}
          >
            <span className="n">{s.n}</span>
            {s.t}
          </a>
        ))}
      </nav>
      <div className="doc-toc-foot">
        <MonoChip tone="ghost">nightswarm.app · beta</MonoChip>
      </div>
    </aside>
  );
}

function Section({
  id,
  children,
}: {
  id: (typeof SECTIONS)[number]["id"];
  children: ReactNode;
}) {
  const meta = SECTIONS.find((s) => s.id === id)!;
  return (
    <section id={id} className="doc-sec">
      <div className="doc-kicker">
        {meta.n} · {meta.t}
      </div>
      {children}
    </section>
  );
}

/** The collapsible advanced layer — precise mechanics, opt-in. */
function Mech({
  title = "Under the hood",
  children,
}: {
  title?: string;
  children: ReactNode;
}) {
  return (
    <details className="doc-mech">
      <summary>
        <span className="chev">
          <Icon d={I.chevR} size={13} />
        </span>
        {title}
      </summary>
      <div className="doc-mech-body doc-prose">{children}</div>
    </details>
  );
}

function Code({ title, children }: { title?: string; children: string }) {
  return (
    <figure className="doc-code">
      {title && (
        <figcaption>
          <Icon d={I.terminal} size={12} />
          {title}
        </figcaption>
      )}
      <pre>{children}</pre>
    </figure>
  );
}

function Callout({ children }: { children: ReactNode }) {
  return (
    <div className="doc-callout">
      <span className="ic">
        <Icon d={I.zap} size={16} />
      </span>
      <div>{children}</div>
    </div>
  );
}

/* ── 01 · Concepts ───────────────────────────────────────────────── */

function ConceptMap() {
  return (
    <>
      <div className="doc-map" role="img" aria-label="NightSwarm overview: you direct from the Claude app or dashboard, the coordination plane holds schedules and the queue, and your own machines run the agents.">
        <div className="doc-map-node">
          <div className="head">
            <Icon d={I.user} size={16} />
            <h3>You</h3>
          </div>
          <p>
            Direct the swarm from wherever you already are — and review what
            it did in the morning.
          </p>
          <div className="items">
            <MonoChip tone="ghost">Claude app</MonoChip>
            <MonoChip tone="ghost">dashboard</MonoChip>
            <MonoChip tone="ghost">push</MonoChip>
          </div>
        </div>
        <span className="doc-map-arrow">
          <span className="h">
            <Icon d={I.arrowR} size={16} />
          </span>
          <span className="v">
            <Icon d={I.arrowDown} size={16} />
          </span>
        </span>
        <div className="doc-map-node">
          <div className="head">
            <SwarmMoon size={16} />
            <h3>The plane — nightswarm.app</h3>
          </div>
          <p>
            A coordination layer: it remembers your agents, fires schedules,
            queues jobs, archives transcripts, and pushes when you're needed.
          </p>
          <div className="items">
            <MonoChip tone="accent">no AI runs here</MonoChip>
            <MonoChip tone="ghost">schedules · queue</MonoChip>
          </div>
        </div>
        <span className="doc-map-arrow">
          <span className="h">
            <Icon d={I.arrowR} size={16} />
          </span>
          <span className="v">
            <Icon d={I.arrowDown} size={16} />
          </span>
        </span>
        <div className="doc-map-node">
          <div className="head">
            <Icon d={I.cpu} size={16} />
            <h3>Your machines</h3>
          </div>
          <p>
            Macs you own, each running a small daemon that supervises live
            coding-agent sessions — Claude Code and Codex, side by side. All
            the thinking happens here.
          </p>
          <div className="items">
            <MonoChip tone="accent">all AI runs here</MonoChip>
            <MonoChip tone="ghost">claude · codex</MonoChip>
          </div>
        </div>
      </div>
      <div className="doc-map-note">
        messages flow left → right · transcripts and results flow back
      </div>
    </>
  );
}

function Concepts() {
  return (
    <Section id="concepts">
      <h2>Six words, one picture.</h2>
      <p className="doc-lead">
        NightSwarm coordinates your coding agents — Claude Code and Codex —
        running on your own computers. You describe the work and when it
        should happen; your machines do it; you wake up to results. Everything
        in the product is built from six ideas.
      </p>
      <ConceptMap />
      <dl className="doc-terms">
        <div className="doc-term">
          <dt>machine</dt>
          <dd>
            A Mac you enroll. It connects outward to NightSwarm — nothing
            connects into it — and reports what it can do: which skills, MCP
            servers, and agent definitions live on it. (MCP — Model Context
            Protocol — is the plug-in standard that connects Claude to
            outside tools like Slack or your calendar; an "MCP server" is one
            such plug-in.)
          </dd>
        </div>
        <div className="doc-term">
          <dt>agent</dt>
          <dd>
            A named, persistent coding-agent workspace — "bookkeeper",
            "research" — pinned to a machine (or "any") with a working
            directory. The directory is the agent's personality: its
            instructions, permissions, and files live there. Each agent has a
            <strong> runtime</strong> — <code>claude</code> (the default),{" "}
            <code>codex</code>, or <code>any</code> — choosing which coding-agent
            CLI it runs on.
          </dd>
        </div>
        <div className="doc-term">
          <dt>session</dt>
          <dd>
            One live coding-agent conversation (Claude Code or Codex)
            belonging to an agent. Messages route into the agent's current
            session, so context accumulates — or you can ask for a fresh one.
          </dd>
        </div>
        <div className="doc-term">
          <dt>schedule</dt>
          <dd>
            "Every night at 2am, tell research to write the digest." A
            timezone-aware recurring rule that creates jobs.
          </dd>
        </div>
        <div className="doc-term">
          <dt>job</dt>
          <dd>
            One unit of dispatched work. If the machine is asleep or offline,
            the job waits in the queue and retries for about an hour until it
            can be delivered.
          </dd>
        </div>
        <div className="doc-term">
          <dt>team</dt>
          <dd>
            A shared space. Teammates see the same machines, agents,
            schedules, queue, and transcripts — and share the morning review.
          </dd>
        </div>
      </dl>
      <Callout>
        <p>
          <strong>The hard rule: NightSwarm itself never calls an AI
          model.</strong> The plane stores, schedules, routes, and notifies —
          that's all. Every bit of inference runs on your own machines,
          through the existing Claude and ChatGPT subscriptions you already
          pay for — Claude Code work on your Claude seat, Codex work on your
          ChatGPT seat. Your prompts and your agents' thinking never pass
          through a third-party model on our side, and there is no per-token
          bill from NightSwarm.
        </p>
      </Callout>
      <Mech title="Under the hood — architecture & design decisions">
        <p>
          The plane is serverless AWS: API Gateway (WebSocket + HTTP),
          DynamoDB, S3, EventBridge Scheduler, and Web Push. Machines hold a
          single <strong>outbound-only WebSocket</strong> to the plane — no
          inbound ports, no VPN, works behind home NAT.
        </p>
        <ul>
          <li>
            The capability scanner reports <em>names and descriptions
            only</em> for your MCP servers, skills, and agents. Commands,
            environment variables, and URLs never leave the machine — your
            API keys and tokens stay where they are.
          </li>
          <li>
            Agents are <strong>interactive</strong> coding-agent sessions
            (Claude Code or Codex), not headless runs. You can open any
            session mid-flight, watch the live transcript, type into it,
            approve a prompt, or stop it — exactly as if you were sitting at
            that terminal.
          </li>
          <li>
            Integrations (GitHub, Linear, Slack, calendars…) are
            machine-local skills and MCP servers, never plane configuration.
            The plane can't hold credentials it never receives.
          </li>
          <li>
            Autonomy is earned, rung by rung: start read-only (R0), let
            agents draft PRs and reports for your review (R1), and only widen
            permissions after the morning reviews prove them out.
            Review-first is the default posture everywhere.
          </li>
        </ul>
      </Mech>
    </Section>
  );
}

/* ── 02 · Getting started ────────────────────────────────────────── */

function GettingStarted() {
  return (
    <Section id="getting-started">
      <h2>First machine in a few minutes.</h2>
      <p className="doc-lead">
        There's nothing to configure server-side — and nothing to install
        besides the app itself. Sign in, put NightSwarm on a Mac, enroll
        it, and watch it appear in your swarm.
      </p>
      <div className="doc-steps">
        <div className="doc-step">
          <div>
            <h3>Sign in</h3>
            <p>
              Enter your email on the home page and open the magic link we
              send you. No password, no credit card.
            </p>
          </div>
        </div>
        <div className="doc-step">
          <div>
            <h3>Install the app</h3>
            <p>
              From the dashboard's <strong>Swarm</strong> tab (your
              machines and agents), download NightSwarm for Mac (Apple
              Silicon). Open the <code>.dmg</code> and drag NightSwarm
              into Applications, then open it — the swarm icon appears in
              the menu bar.
            </p>
            <p>
              The app is signed and notarized, and it carries everything
              the daemon needs — its own runtime included. There is
              nothing else to install.
            </p>
          </div>
        </div>
        <div className="doc-step">
          <div>
            <h3>Enroll the Mac</h3>
            <p>
              Click the swarm icon → <strong>Set up this Mac</strong>. It
              opens the Swarm tab, which shows a one-line enroll command
              that already includes your sign-in token — paste it into
              Terminal on that Mac and you're enrolled.
            </p>
            <p>
              Then, back in the menu bar, click{" "}
              <strong>Install daemon</strong> — the daemon starts under
              launchd and comes back on its own after reboots. From then
              on the menu bar offers Start/Stop/Restart.
            </p>
          </div>
        </div>
        <div className="doc-step">
          <div>
            <h3>Grant Full Disk Access</h3>
            <p>
              If you want agents that read Mail, Messages, or Safari data,
              grant <strong>Full Disk Access</strong> to{" "}
              <strong>NightSwarm.app</strong>: the menu bar offers{" "}
              <strong>Grant Full Disk Access</strong>, which opens the
              System Settings pane with the app revealed — drag it in and
              the daemon confirms the grant. Skip this if your agents only
              touch files and repos.
            </p>
          </div>
        </div>
        <div className="doc-step">
          <div>
            <h3>Watch it appear</h3>
            <p>
              Within seconds the machine card flips to online in the Swarm tab, with
              chips for every skill, MCP server, and agent definition the
              scanner found on it. The menubar icon shows
              "Connected" and lists any active sessions.
            </p>
            <p>
              The scan also detects which coding-agent CLIs are installed:{" "}
              <strong>Claude Code</strong> always, and{" "}
              <strong>Codex</strong> (OpenAI's <code>codex</code> CLI) when it's
              present and logged in to a ChatGPT seat. A machine that reports
              both runs Claude and Codex sessions side by side — and the{" "}
              <code>codex</code> and <code>any</code> runtime options unlock on
              your agents.
            </p>
          </div>
        </div>
      </div>
      <Mech title="Advanced — CLI enrollment with nightswarmd">
        <p>
          The menubar app is a thin supervisor over the daemon. If you'd
          rather skip it (a headless Mac mini, say), run the daemon
          directly:
        </p>
        <Code title="terminal — on the Mac you're enrolling">
          {`nightswarmd enroll --base-url https://nightswarm.app --jwt <your token>
nightswarmd run --ws-url wss://q5xh460ih2.execute-api.us-east-1.amazonaws.com/prod`}
        </Code>
        <ul>
          <li>
            <code>enroll</code> registers the machine once and writes
            credentials to <code>~/.nightswarm/credentials.json</code>{" "}
            (mode 0600). Add <code>--team &lt;id&gt;</code> to enroll the
            machine into a team instead of your personal space.
          </li>
          <li>
            <code>--ws-url</code> is required on the <em>first</em>{" "}
            <code>run</code>: enrollment doesn't return the WebSocket URL,
            and without it the daemon exits with "no WebSocket URL". The
            URL above is the live nightswarm.app endpoint. It's persisted
            into the credentials file, so every later{" "}
            <code>nightswarmd run</code> — including the menubar's Start
            daemon — needs no flags.
          </li>
          <li>
            <code>run</code> is the long-lived loop: outbound WebSocket,
            heartbeats every few minutes, automatic reconnect with backoff.
            While disconnected it buffers outbound events to disk and
            flushes them in order on reconnect — transcripts survive network
            blips.
          </li>
          <li>
            The menubar app and the daemon are independent: quitting the
            menubar never stops the daemon. The menubar just reads the
            daemon's state file and offers Start/Stop, session links, and a
            dashboard shortcut.
          </li>
          <li>
            Transitional: the previous Electron menubar build remains
            available as a zip at{" "}
            <code>/downloads/NightSwarm-mac-arm64-latest.zip</code> during
            the changeover. Unlike the .dmg, it relies on Node 22+ already
            being on the machine — prefer the .dmg.
          </li>
        </ul>
      </Mech>
    </Section>
  );
}

/* ── 03 · Configuring agents ─────────────────────────────────────── */

function ConfiguringAgents() {
  return (
    <Section id="agents">
      <h2>An agent is a directory with a job description.</h2>
      <p className="doc-lead">
        Create an agent in two places: the dashboard (the Swarm tab → "+ new agent")
        or straight from the Claude app ("create a bookkeeper agent on my
        mini"). Either way you pick a name, a machine, and — the important
        part — a <strong>working directory</strong>.
      </p>
      <div className="doc-prose">
        <p>
          NightSwarm itself stores almost nothing about how an agent behaves.
          Behavior comes from what's <em>in the directory</em> on your
          machine, the same way it does when you run Claude Code by hand:
        </p>
        <ul>
          <li>
            <strong><code>CLAUDE.md</code></strong> — the agent's role,
            ground rules, and where to write things. Claude Code reads it at
            the start of every session.
          </li>
          <li>
            <strong><code>.claude/settings.local.json</code></strong> — the
            permission allowlist. In plain terms: you pre-approve the tools
            this agent may use so it never stalls mid-run asking "may I?".
          </li>
          <li>
            Whatever <strong>skills and MCP servers</strong> the machine has
            — the agent inherits the machine's toolbox.
          </li>
        </ul>
        <p>
          Both files are reachable from the dashboard: open an agent and
          switch to its <strong>Instructions</strong> tab to read and edit{" "}
          <code>CLAUDE.md</code> in place. The daemon on the agent's machine
          does the file I/O and echoes back exactly what landed on disk —
          the plane never touches your files. The permission allowlist shows
          there too, <em>read-only</em>: grants are managed on the machine
          itself, so a browser session can never widen what an agent may do.
        </p>
      </div>

      <h3>Worked example: a bookkeeper</h3>
      <div className="doc-prose">
        <p>
          Make a folder, give it a job description, pre-approve its tools,
          then create the agent pointing at it. Two small files:
        </p>
      </div>
      <Code title="~/agents/bookkeeper/CLAUDE.md">
        {`# Bookkeeper

You are the household bookkeeper for this directory. You run
unattended at night — leave a paper trail, never guess silently.

## Role
- Reconcile new transaction exports in ./inbox/ against ./ledger/
- Keep ./ledger/2026.csv current — one row per transaction
- Write a short nightly summary to ./reports/YYYY-MM-DD.md

## Ground rules
- Files only. Never log in to anything. Never move money.
- Anything ambiguous goes in ./reports/needs-review.md — flag it
  and move on instead of stalling.
- Keep summaries under 20 lines. Lead with anything unusual.`}
      </Code>
      <Code title="~/agents/bookkeeper/.claude/settings.local.json">
        {`{
  "permissions": {
    "allow": [
      "Read(./**)",
      "Write(./ledger/**)",
      "Write(./reports/**)",
      "Bash(ls:*)",
      "Bash(mv:*)"
    ]
  }
}`}
      </Code>
      <div className="doc-prose">
        <p>
          The allowlist is the difference between an agent that finishes at
          3am and one that sits frozen on a permission prompt until you wake
          up. Grant exactly what the role needs — read everywhere in its
          folder, write only to its output folders, a couple of shell
          commands — and nothing else. Every allowlist entry is also a
          guardrail: the bookkeeper above <em>can't</em> write outside{" "}
          <code>./ledger/</code> and <code>./reports/</code>.
        </p>
      </div>

      <h3>Running an agent on Codex</h3>
      <div className="doc-prose">
        <p>
          Every agent has a <strong>runtime</strong>: <code>claude</code> (the
          default), <code>codex</code>, or <code>any</code>. Set it in the
          agent sheet — the <code>codex</code> and <code>any</code> options
          appear once a machine reports the <code>codex</code> CLI installed
          and logged in to a ChatGPT seat. Pick <code>codex</code> to pin an
          agent to OpenAI's Codex CLI; pick <code>any</code> and dispatch
          chooses per machine availability and your tenant's runtime
          preference (Claude first by default, Codex as fallback). The single-
          machine payoff: if your Claude seat hits its 5-hour limit at 2am, an{" "}
          <code>any</code> agent's job runs on the <em>same</em> machine's
          Codex seat instead of waiting.
        </p>
        <ul>
          <li>
            <strong><code>AGENTS.md</code></strong> is Codex's instruction
            file, the way <code>CLAUDE.md</code> is Claude Code's — Codex reads
            it from the agent's working directory at the start of every
            session. Keep it in the agent's repo alongside the directory; it's
            managed on the machine (the dashboard's Instructions tab edits{" "}
            <code>CLAUDE.md</code>). An <code>any</code> agent whose directory
            holds both files works on either runtime — each CLI reads its own.
          </li>
          <li>
            <strong><code>config.toml</code></strong> (in <code>~/.codex/</code>)
            holds Codex's own defaults like <code>model</code> and{" "}
            <code>model_reasoning_effort</code>. NightSwarm passes the agent's
            model and effort explicitly at spawn, so the per-agent settings win
            over the file.
          </li>
          <li>
            <strong>Subscription &amp; limits:</strong> Codex sessions bill to
            your ChatGPT seat, never to NightSwarm. Codex reports its own seat
            and usage in-band, so per-service usage and seat-limit state show
            split by runtime — Claude vs Codex — in <code>swarm_status</code>,{" "}
            <code>token_usage</code>, and the dashboard.
          </li>
        </ul>
        <Callout>
          <p>
            <strong>Codex fidelity — what differs from Claude Code.</strong>{" "}
            Goal mode maps to Codex's native <code>/goal</code>. Loop mode is{" "}
            <em>emulated</em> — Codex has no <code>/loop</code>, so NightSwarm
            prepends a plain-text "work autonomously toward this goal"
            instruction instead. Codex command-approval dialogs{" "}
            <strong>escalate to you</strong> (a needs-human prompt) rather than
            auto-approving — a deliberate safety choice. Model and reasoning
            effort are fixed when the session spawns; changing them mid-session
            isn't supported (start a fresh session). And transcript and token
            detail differ between the two runtimes.
          </p>
        </Callout>
      </div>

      <Mech title="Advanced — pinning, warm start, and routing">
        <ul>
          <li>
            <strong>Pinning:</strong> an agent pinned to a machine always
            runs there. Pin to <code>any</code> and NightSwarm picks the
            most recently seen online machine in your tenant — useful for
            agents whose directory exists on several Macs.
          </li>
          <li>
            <strong>Warm start:</strong> mark an agent <em>warm</em> and the
            daemon starts a session for it as soon as the machine connects
            — the agent is already booted, folder-trusted, and waiting when
            the first message arrives.
          </li>
          <li>
            <strong>Routing:</strong> a message to an agent goes to its
            current live session if one is running and the machine is
            online; otherwise a new session starts in the agent's working
            directory with your message as the opening prompt. Agent names
            are lowercase <code>a–z 0–9 -</code>, up to 64 characters.
          </li>
          <li>
            <strong>From the Claude app:</strong> the connector exposes full
            agent CRUD (<code>create_agent</code>, <code>update_agent</code>,{" "}
            <code>delete_agent</code> — deletion requires explicit
            confirmation). The agents widget shows the roster with live
            status and per-agent inline messaging.
          </li>
        </ul>
      </Mech>
    </Section>
  );
}

/* ── 04 · Talking to agents ──────────────────────────────────────── */

function TalkingToAgents() {
  return (
    <Section id="talking">
      <h2>Same agents, two doors.</h2>
      <p className="doc-lead">
        Everything you can do from the dashboard you can also do from a
        Claude conversation — on your phone, in the desktop app, wherever
        you already chat.
      </p>
      <h3>From the dashboard</h3>
      <div className="doc-prose">
        <p>
          Open the dashboard's <strong>Swarm</strong> tab, tap an agent, type a message. The
          reply streams into a live session view — every tool call,
          every file written — under <strong>Activity</strong>, where you can
          keep the conversation going with the composer, stop the session,
          or approve anything the agent asks about. The dashboard installs
          as an app (PWA) on your phone and supports push notifications.
        </p>
      </div>
      <h3>From the Claude app</h3>
      <div className="doc-prose">
        <ol>
          <li>
            In the Claude app, open <strong>Settings → Connectors → Add
            custom connector</strong>.
          </li>
          <li>
            Enter <code>https://nightswarm.app/mcp</code>.
          </li>
          <li>
            Approve the connection — it signs you in with the same email
            magic link as the website. No API keys, nothing to copy.
          </li>
        </ol>
        <p>
          From then on you can just talk: <em>"ask the bookkeeper how last
          night went"</em>, <em>"what's my swarm doing?"</em>, <em>"schedule
          the digest for weeknights at 2"</em>. Tool responses render as
          live, interactive widgets right in the thread — swarm status,
          session status with approvals, the schedule editor, the agent
          roster with inline messaging, the queue, token burn, and the
          morning review — in light and dark. See all eight running with
          demo data in the <Link to="/widgets">widget gallery</Link>.
        </p>
      </div>
      <Mech title="Advanced — ask_agent semantics and the session model">
        <p>
          The connector exposes 44 tools covering status, messaging, jobs,
          session management, agent CRUD, schedule CRUD (including
          no-side-effect drafts), review, token usage, and teams. The ones
          worth knowing by name:
        </p>
        <ul>
          <li>
            <code>ask_agent</code> routes your message into the agent's
            current session (context accumulates) or starts one, then waits
            up to ~20 seconds for the reply. If the agent is mid-thought,
            you get "delivered — reply pending" with the session id to check
            back on.
          </li>
          <li>
            <code>ask_agent {`{newSession: true}`}</code> starts a fresh
            session — clean context — which becomes the agent's new default.
            Pass an explicit <code>sessionId</code> to speak into an older
            still-running session instead. <code>list_sessions</code> finds
            resumable ones.
          </li>
          <li>
            <code>run_job</code> is fire-and-forget for long work: it
            enqueues, returns immediately, and the job queues/retries if the
            machine is offline.
          </li>
          <li>
            <code>get_session_transcript</code> /{" "}
            <code>session_status</code> read any session;{" "}
            <code>stop_session</code> halts one.
          </li>
        </ul>
      </Mech>
      <Mech title="Advanced — teach Claude your swarm with a skill file">
        <p>
          The connector gives Claude the <em>tools</em>; a small{" "}
          <strong>skill file</strong> teaches it your <em>routing</em> — so
          you can say "research X" instead of "use the nightswarm connector
          to run_job to my research agent". A skill is one markdown file
          uploaded to Claude (Settings → Capabilities → Skills) with YAML
          frontmatter and a body:
        </p>
        <ul>
          <li>
            <strong>frontmatter <code>description:</code></strong> controls{" "}
            <em>when</em> the skill fires — name your trigger phrases there
            ("research…", "schedule…", "what's my swarm doing", "save
            this").
          </li>
          <li>
            <strong>an agent roster table</strong> — one row per agent,
            "send X there" in plain words. Keep it in sync with{" "}
            <code>list_agents</code>; note sensitive domains explicitly so
            Claude routes rather than summarizes.
          </li>
          <li>
            <strong>decision rules</strong> — when to answer directly, when
            to <code>ask_agent</code> (and wait), when to{" "}
            <code>run_job</code> (fire-and-forget), when to{" "}
            <code>create_schedule</code>.
          </li>
          <li>
            <strong>delivery preferences</strong> — e.g. "for Discord
            updates, include the chat_id in the delegated prompt" (see the
            chat-notifications recipe in{" "}
            <a href="#workflows">common workflows</a>).
          </li>
        </ul>
        <p>
          Division of labor: the skill file routes; each agent's CLAUDE.md
          (editable from here via <code>write_agent_file</code>) says how
          that agent works. Add an agent → add a roster row → re-upload the
          skill. A complete worked example lives in the NightSwarm repo at{" "}
          <code>skills/nightswarm/SKILL.md</code>.
        </p>
      </Mech>
    </Section>
  );
}

/* ── 05 · Dispatch profiles ──────────────────────────────────────── */

function DispatchProfilesDoc() {
  return (
    <Section id="dispatch">
      <h2>Match the model to the task — on every dispatch.</h2>
      <p className="doc-lead">
        Every dispatch — <code>ask_agent</code>, <code>run_job</code>, or a
        schedule — carries a <strong>profile</strong>: a model, a reasoning
        effort, and an intensity. Claude self-routes by task weight, so haiku
        handles the chores and opus at max effort is saved for the runs that
        earn it.
      </p>
      <div className="doc-prose">
        <p>The three dials:</p>
        <ul>
          <li>
            <strong>model</strong> — <code>haiku</code>, <code>sonnet</code>,
            or <code>opus</code> (or a raw model id). On a Codex agent this
            picks the codex model instead.
          </li>
          <li>
            <strong>effort</strong> — reasoning depth,{" "}
            <code>low · medium · high · xhigh · max</code>. Claude Code's{" "}
            <code>/effort</code>; on Codex, the reasoning level
            (<code>max</code> clamps to <code>xhigh</code>, Codex's ceiling).
          </li>
          <li>
            <strong>intensity</strong> — <em>how</em> the agent works:
            <ul>
              <li>
                <code>light</code> — mechanical work. No subagents, minimal
                exploration, don't overthink.
              </li>
              <li>
                <code>standard</code> — everyday behavior. No preamble;
                exactly what you'd get running the CLI by hand.
              </li>
              <li>
                <code>deep</code> — thorough. Explore with subagents,
                adversarially check important claims, verify before claiming
                done.
              </li>
              <li>
                <code>swarm</code> — workflow-driven development. One dispatch
                becomes a fleet: parallel finders on cheap models, judge
                panels, skeptic agents whose only job is to refute, and a
                completeness critic before any phase is called done.
              </li>
            </ul>
          </li>
        </ul>
      </div>

      <h3>Just say it — Claude picks the profile</h3>
      <div className="doc-prose">
        <p>
          You don't fill in forms. The routing rubric is baked into the MCP
          tool definitions, so plain language maps to a profile:
        </p>
      </div>
      <Code title="From the Claude app">
        {`"Have research rename the feed-flag config key tonight
 — keep it light."
   → run_job · haiku · low · light

"Debug why the auth test flakes. Go deep and prove the fix."
   → run_job · opus · high · deep

"Audit roundtable for race conditions overnight. Be exhaustive."
   → run_job · opus · max · swarm`}
      </Code>

      <h3>Pin an agent's default</h3>
      <div className="doc-prose">
        <p>
          Agents carry profile defaults for runs that don't choose — set them
          in the agent sheet or from chat. A nightly auditor pinned to{" "}
          <code>swarm</code> runs every scheduled fire as a multi-agent
          workflow without anyone asking:
        </p>
      </div>
      <Code title="A nightly auditor, pinned to swarm">
        {`"Create an auditor agent on studio-mini at ~/agents/auditor,
 default it to opus, max effort, swarm intensity, and schedule
 'sweep the monorepo for regressions' weeknights at 2."

→ create_agent  { model: "opus", effort: "max", intensity: "swarm" }
→ create_schedule { cron: "0 2 * * MON-FRI", agent: "auditor" }`}
      </Code>
      <Callout>
        <p>
          <strong>What swarm actually does.</strong> The plane prepends a
          versioned preamble to the prompt — never an extra LLM call — that
          opts the session into multi-agent orchestration: fan out parallel
          researchers, run judge panels over competing attempts, adversarially
          verify every important claim, and gate completion on a completeness
          critic. Sub-fleet economics ride along: finders on haiku/sonnet at
          low effort, judgment and synthesis on the session's own model.{" "}
          <code>standard</code> injects nothing — byte-identical to what you
          ran before profiles existed.
        </p>
      </Callout>
      <Mech title="Advanced — resolution, rolling sessions, and Codex">
        <ul>
          <li>
            <strong>Resolution chain:</strong> per-run value → agent default →{" "}
            <code>standard</code>. Schedules persist their profile and every
            fired job carries it.
          </li>
          <li>
            <strong>Rolling sessions:</strong> an agent's ongoing{" "}
            <code>ask_agent</code> session only gets a preamble when you pass
            intensity <em>explicitly on that call</em> — agent defaults never
            re-assert mid-conversation, so a swarm-pinned auditor still chats
            normally.
          </li>
          <li>
            <strong>Codex:</strong> <code>max</code> effort clamps to{" "}
            <code>xhigh</code> (Codex's ceiling) wherever the run lands on a
            codex runtime, and the swarm preamble swaps to a runtime-neutral
            variant.
          </li>
          <li>
            <strong>Visibility:</strong> session rows stamp the resolved
            model, effort, and intensity — chips in the dashboard's Activity
            and machine views, plus <code>session_status</code> in chat. The
            preamble version is stamped too, so transcripts stay attributable.
          </li>
          <li>
            <strong>Budget:</strong> swarm runs more subagents, so
            swarm-heavy schedules pair naturally with{" "}
            <code>set_budget</code> night-window caps. Cheap-model fan-out is
            the per-session cost control.
          </li>
        </ul>
      </Mech>
    </Section>
  );
}

/* ── 06 · Schedules ──────────────────────────────────────────────── */

function Schedules() {
  return (
    <Section id="schedules">
      <h2>Describe when; the editor writes the cron.</h2>
      <p className="doc-lead">
        A schedule is "this agent, this instruction, this rhythm." The
        editor is built for humans: pick <strong>nightly</strong>,{" "}
        <strong>weekdays</strong>, custom <strong>day pills</strong>, or an
        interval; set the time with steppers; watch the live preview update.
        The same editor opens in the dashboard and as a widget inside the
        Claude app.
      </p>
      <div className="doc-prose">
        <p>Each schedule has a mode — three ways to hand work over:</p>
        <ul>
          <li>
            <strong>message</strong> (default) — one instruction, delivered
            as if you'd typed it. "Write tonight's digest."
          </li>
          <li>
            <strong>goal</strong> — give the agent an objective and let it
            work autonomously until it's done. Best for open-ended jobs:
            "get the test suite green."
          </li>
          <li>
            <strong>loop</strong> — repeat the instruction on an interval
            within one session ("every 10m: check the deploy queue"), or let
            the agent pace itself.
          </li>
        </ul>
        <p>
          A schedule runs on its agent's <strong>runtime</strong> — set on the
          agent (<code>claude</code>, <code>codex</code>, or <code>any</code>),
          so the same humane editor drives a Claude Code or a Codex session
          without changing how you describe the work.
        </p>
        <p>
          Schedules are timezone-aware — set your zone once and "2:00 AM"
          means <em>your</em> 2:00 AM through DST changes. When a schedule
          fires it creates a <strong>job</strong>; if the machine is offline
          the job waits in the queue and retries for about an hour, so a
          laptop that wakes within that window gets its work as soon as it
          comes online. If it stays offline longer, the job is marked
          failed — one tap in the queue retries it.
        </p>
      </div>
      <Mech title="Advanced — raw cron and dispatch mechanics">
        <ul>
          <li>
            Behind the humane controls is a real 6-field cron expression
            (EventBridge Scheduler syntax) you can hand-edit — the editor
            round-trips between the pills and the raw string. Default
            timezone is America/New_York until you change it.
          </li>
          <li>
            Goal and loop modes map to Claude Code's native{" "}
            <code>/goal</code> and <code>/loop &lt;interval&gt;</code>{" "}
            commands — the daemon types them into the session verbatim. On a
            Claude Code version without the command, the text lands as a
            normal prompt: harmless degradation. On <strong>Codex</strong>,
            goal mode maps to its native <code>/goal</code>; loop mode is
            emulated with a prepended plain-text instruction block, since Codex
            has no <code>/loop</code> (see section 03).
          </li>
          <li>
            Queued jobs are re-dispatched every 2 minutes, up to 30
            attempts (about an hour), before being marked failed ("machine
            never came online"). Failed jobs can be retried with one tap
            from the queue (or the queue widget in the Claude app).
          </li>
          <li>
            From the Claude app, <code>draft_schedule</code> opens the
            editor widget pre-filled with no side effects — you adjust and
            save from the widget itself.
          </li>
        </ul>
      </Mech>
    </Section>
  );
}

/* ── 07 · When an agent needs you ────────────────────────────────── */

function NeedsYou() {
  return (
    <Section id="needs-you">
      <h2>Unattended, not unsupervised.</h2>
      <p className="doc-lead">
        A coding agent occasionally stops and asks — trust this folder? run
        this tool? The daemon handles the routine ones itself, and
        escalates to your phone only when a human genuinely has to decide.
      </p>
      <div className="doc-prose">
        <p>What happens, in order:</p>
        <ol>
          <li>
            <strong>Known prompts answer themselves.</strong> The daemon
            recognizes Claude Code's standard blocking dialogs — folder
            trust, tool approvals, overwrite confirmations — and answers
            them so the night's work doesn't stall. When an approval menu
            offers "yes, and don't ask again", it prefers that option, so
            each interruption happens at most once per project. On{" "}
            <strong>Codex</strong>, command-approval dialogs are{" "}
            <em>not</em> auto-answered — they escalate to you instead (a
            deliberate safety choice for the Codex runtime).
          </li>
          <li>
            <strong>Anything else escalates.</strong> If a session goes
            quiet on a prompt the daemon doesn't recognize, you get a push
            notification. It links straight to the session page.
          </li>
          <li>
            <strong>You approve from anywhere.</strong> The session page
            shows exactly what's on the agent's screen and an{" "}
            <strong>approval bar</strong> with the prompt's options —
            choose one and the keystroke is sent into the live session.
            On tool-approval menus, the "don't ask again" option grants
            that tool permanently for that project, so tomorrow night it
            won't ask at all.
          </li>
        </ol>
        <p>
          The best escalation is the one that never fires: a good permission
          allowlist in <code>.claude/settings.local.json</code> (section 03)
          pre-approves everything the agent's role needs, which prevents
          most of these prompts from ever appearing.
        </p>
      </div>
      <Mech title="Under the hood — supervision details">
        <ul>
          <li>
            <strong>Readiness-gated delivery:</strong> when a session
            starts, the daemon waits for Claude Code's input box to actually
            be ready — answering boot dialogs along the way — before typing
            your prompt. Prompts don't get eaten by a half-booted TUI.
          </li>
          <li>
            <strong>One-shot redelivery:</strong> if the transcript hasn't
            appeared after delivery (the rare eaten prompt), the daemon
            re-sends it exactly once and records that it did.
          </li>
          <li>
            <strong>Bounded auto-answers:</strong> auto-answering has a
            per-session safety cap; past it, the session escalates to a
            human instead of clicking forever.
          </li>
          <li>
            <strong>One ping per stall:</strong> needs-human fires once per
            stuck episode and re-arms when the session moves again — no
            notification storms. In a team, every member is notified.
          </li>
        </ul>
      </Mech>
    </Section>
  );
}

/* ── 08 · Teams ──────────────────────────────────────────────────── */

function Teams() {
  return (
    <Section id="teams">
      <h2>One swarm, shared by the people who review it.</h2>
      <p className="doc-lead">
        A team is a shared tenant: same machines, same agents, same
        schedules, same queue, same transcripts. Create one from the tenant
        switcher in the dashboard header ("+ New team"), add teammates by
        email, and they see the team next time they sign in.
      </p>
      <div className="doc-prose">
        <p>
          The ritual that makes it work is the <strong>morning
          review</strong>: a digest of everything that ran overnight —
          sessions, jobs, tokens, anything that needs a human — rendered at
          the top of the dashboard (and available as a widget in the Claude
          app). Each member marks it reviewed; everyone can see who has and
          hasn't. Overnight autonomy stays honest because someone reads the
          output every morning.
        </p>
        <p>
          <strong>Integrations live on machines, never in team
          settings.</strong> GitHub, Linear, Slack, calendars — each is a
          skill or MCP server configured on a team machine, with credentials
          that never touch the plane. Want the morning review delivered to
          Slack? That's not a NightSwarm feature toggle — it's just a
          schedule:
        </p>
      </div>
      <Code title="example — review-to-Slack as an ordinary schedule">
        {`agent     reporter            (on a machine that has the Slack MCP)
repeat    weekdays · 7:00 AM
mode      message
prompt    Compose the morning review for the team — every session
          and job since last evening, what shipped, what failed,
          what needs a human — and post it to #swarm-overnight
          via the Slack MCP.`}
      </Code>
      <Mech title="Advanced — tenancy details">
        <ul>
          <li>
            Your personal space and each team are separate tenants; the
            switcher in the dashboard header moves every view between them.
            Machines enroll into a specific tenant
            (<code>--team &lt;id&gt;</code>), so a work Mac and a personal
            Mac stay cleanly apart.
          </li>
          <li>
            Members have owner or member roles; owners manage membership
            and settings. Team settings are free-form notes for agents
            (conventions, org names) — the plane never interprets them.
          </li>
          <li>
            From the Claude app, every tool accepts an optional{" "}
            <code>team</code> argument; personal is the default.{" "}
            <code>list_teams</code> shows yours.
          </li>
          <li>
            Push is per-user: each member subscribes their own devices, and
            needs-human alerts notify all members of the tenant.
          </li>
        </ul>
      </Mech>
    </Section>
  );
}

/* ── 09 · PR reviews ─────────────────────────────────────────────── */

function PrReviews() {
  return (
    <Section id="pr-reviews">
      <h2>Review pull requests on your own machine.</h2>
      <p className="doc-lead">
        Point a repo's pull requests at one of your agents and every PR gets
        reviewed by a coding agent running on a Mac you own — on the{" "}
        <strong>Claude (or Codex) seat you already pay for</strong>. No GitHub
        Actions minutes, no API tokens stored in the cloud, and the agent has
        your <strong>full local repo context</strong>: it checks the PR out and
        reads the actual code, not just the diff.
      </p>
      <div className="doc-prose">
        <p>
          The review is <strong>advisory</strong>. By default the agent posts
          review <strong>comments</strong> — it never merges, and it doesn't
          submit an approving or blocking review unless you allow that
          (reviewMode, below). It posts as the machine's own GitHub identity —
          the account that machine's <code className="doc-inline-code">gh</code>{" "}
          is logged in as. There's no GitHub App and no bot account: a review
          shows up as <em>you</em>.
        </p>
      </div>

      <h3>Set it up</h3>
      <div className="doc-prose">
        <ol>
          <li>
            <strong>Register the repo</strong> — in the Claude app run{" "}
            <code className="doc-inline-code">register_github_repo</code> (or{" "}
            <code className="doc-inline-code">PUT /github/repos</code>) with the
            repo's <code className="doc-inline-code">owner/name</code> and the{" "}
            <strong>agentName</strong> that should review it. You get back a{" "}
            <strong>webhook URL</strong> (
            <code className="doc-inline-code">
              https://nightswarm.app/webhook/github
            </code>
            ) and a freshly minted <strong>secret</strong>.
          </li>
          <li>
            <strong>Copy the secret now</strong> — it's shown{" "}
            <strong>once</strong>. Lost it? Run{" "}
            <code className="doc-inline-code">update_github_repo</code> with{" "}
            <code className="doc-inline-code">rotateSecret</code> to mint a new
            one (the old one stops working).
          </li>
          <li>
            <strong>Add the webhook in GitHub</strong> — the repo's{" "}
            <strong>Settings → Webhooks → Add webhook</strong>. Paste the URL as
            the Payload URL, set <strong>Content type</strong> to{" "}
            <MonoChip tone="ghost">application/json</MonoChip>, paste the secret
            into <strong>Secret</strong>, and under "Which events" choose{" "}
            <em>Let me select</em> →{" "}
            <MonoChip tone="ghost">Pull requests</MonoChip>.
          </li>
          <li>
            <strong>Make sure the reviewer agent can do the job</strong> — it
            must run on a machine that has the repo cloned and{" "}
            <code className="doc-inline-code">gh</code> authenticated. Express
            that with the agent's <code className="doc-inline-code">requires</code>{" "}
            capabilities (e.g. <code className="doc-inline-code">gh</code> plus a
            token for the repo being present) or by pinning the agent to that
            machine. Paste the <strong>reviewer recipe</strong> (in{" "}
            <code className="doc-inline-code">
              docs/github-pr-reviewer-recipe.md
            </code>
            ) into the agent's Instructions so it knows how to check out, review,
            and post — with the reviewMode and untrusted-PR rules baked in.
          </li>
        </ol>
        <p>
          That's the whole setup. Open or push to a PR and the matching agent
          reviews it.
        </p>
      </div>

      <Callout>
        <strong>reviewMode — what the agent may post.</strong> Set per repo,
        and it's authoritative (nothing in a PR can change it).{" "}
        <strong>comment</strong> (the default) posts review comments only —
        advisory, never approves, never merges.{" "}
        <strong>approve</strong> lets the agent submit an approving review when
        the code is genuinely sound; <strong>request-changes</strong> lets it
        submit a blocking review. Either way it judges the <em>code</em>, and it
        never merges.
      </Callout>

      <div className="doc-prose">
        <p>
          <strong>New commits re-review.</strong> Push to the branch and the
          new commit gets its own review; a still-queued review of the old
          commit is superseded so you don't get stale feedback.
        </p>
        <p>
          <strong>If no capable machine is online, the review waits.</strong>{" "}
          The job holds in the queue (it's not dropped) and runs the moment a
          machine that has the repo and <code className="doc-inline-code">gh</code>{" "}
          comes online — same budget, seat, and capability gates as any other
          job.
        </p>
      </div>

      <Callout>
        <strong>The PR is untrusted.</strong> A PR's title, branch names,
        description, commit messages, and diff are written by whoever opened
        it — possibly someone hostile. NightSwarm fences all of it as
        DATA-only in the prompt it sends the agent, and the reviewer recipe
        reinforces it: the agent treats PR content as something to review,
        never as instructions. Nothing in a PR can change its reviewMode or
        make it approve, merge, or run commands.
      </Callout>

      <Mech title="Under the hood — events, dispatch, and dedupe">
        <ul>
          <li>
            Reviews fire on <code>pull_request</code> events only —{" "}
            <code>opened</code>, <code>synchronize</code> (a new commit),{" "}
            <code>reopened</code>, and <code>ready_for_review</code>. Draft PRs
            are skipped. Nothing else triggers a review (no issues, no pushes,
            no non-PR events).
          </li>
          <li>
            The webhook HMAC-verifies every delivery against that repo's secret
            (<code>X-Hub-Signature-256</code>, constant-time) before it acts. An
            unregistered or disabled repo, or a bad signature, is rejected — no
            review job. The plane never holds your repo credentials; the secret
            only authenticates the webhook.
          </li>
          <li>
            A verified event becomes a review <strong>job</strong> dispatched
            through the normal gate/queue — budget, seat, and capability /
            availability all apply, exactly like a scheduled job. The job id is
            deterministic over (repo, PR, commit, action), so GitHub's retries
            and rapid re-pushes don't create duplicate reviews.
          </li>
          <li>
            It posts as the machine's <code>gh</code> identity (your GitHub
            account). GitHub-only for now — no GitLab/Bitbucket, no auto-merge,
            no cloud review. See{" "}
            <code>docs/archive/phase-14-github-pr-review.md</code> for the contract.
          </li>
        </ul>
      </Mech>
    </Section>
  );
}

/* ── 10 · Common workflows ───────────────────────────────────────── */

const FLOWS = [
  {
    icon: I.moon,
    t: "Personal research assistant",
    b: "A topic you care about, digested every night into a notes folder you actually read.",
    claudeMd: `# Research assistant
Track <your topic> and keep ./notes/ current.

- Each night: find what changed since the last digest
- Append a dated digest: ./notes/digest-YYYY-MM-DD.md
- Maintain a running ./notes/open-questions.md
- Cite sources as plain links. Bullets over prose.`,
    meta: ["nightly · 2:00 AM", "mode · message"],
    prompt:
      "Write tonight's digest. Check open-questions.md first and try to close one.",
    note: null,
  },
  {
    icon: I.wrench,
    t: "Repo maintenance night shift",
    b: "Dependency bumps land as draft PRs while you sleep. Review-first: a human merges in the morning.",
    claudeMd: `# Night maintainer
You maintain this repo while the team sleeps.
Review-first: DRAFT PRs only — a human merges.

- Bump patch/minor dependency updates only
- Run the full test suite before proposing anything
- One concern per PR, with a summary of what changed
- Use the gh CLI. NEVER push to main. NEVER merge.`,
    meta: ["nightly · 3:00 AM", "mode · goal"],
    prompt:
      "Check for outdated dependencies. For each safe update that passes tests, open one draft PR.",
    note: "Needs the gh CLI authenticated on that machine — the integration lives there, not on the plane.",
  },
  {
    icon: I.calendar,
    t: "Meeting-prep briefer",
    b: "A brief on today's meetings, waiting in a folder before your first coffee.",
    claudeMd: `# Briefer
Prepare the morning meeting brief.

- Pull today's events from the calendar MCP
- Per meeting: who, what, the one thing to read first
- Write ./briefs/YYYY-MM-DD.md, first meeting on top
- Flag conflicts and back-to-backs at the very top`,
    meta: ["weekdays · 6:30 AM", "mode · message"],
    prompt: "Write today's brief.",
    note: "Uses a machine-local calendar MCP — its credentials never leave the Mac.",
  },
  {
    icon: I.send,
    t: "Content pipeline",
    b: "A folder of rough notes in; one workable draft out, every week.",
    claudeMd: `# Drafter
Turn ./notes/ into publishable drafts.

- Weekly: pick the most developed idea in ./notes/inbox/
- Draft 800-1200 words into ./drafts/ with a working title
- Move the source note to ./notes/used/
- End with editing notes: what's weak, what to verify`,
    meta: ["weekly · Sun 9:00 PM", "mode · goal"],
    prompt: "Produce this week's draft.",
    note: null,
  },
  {
    icon: I.list,
    t: "Backlog worker",
    b: "A shared task list the swarm drains overnight — and every claim doubles as a health check on stuck teammates.",
    claudeMd: `# Backlog worker
Drain the NightSwarm task backlog via its MCP tools.

- claim_next_task returns {task, swarmHealth} — health first
- For each stuck session on ANOTHER machine: read the blocked
  prompt (session_status / get_session_transcript). Answer via
  respond_to_prompt ONLY if it's safe and obvious (folder
  trust, y/N continuation); otherwise leave it for a human.
  One attempt per stuck session — never ping-pong.
- Work the task, then complete_task with a short summary
  (release_task if you have to give up cleanly)
- Stop when there are no open tasks or budgetRemaining says so`,
    meta: ["nightly · 1:00 AM", "mode · goal"],
    prompt:
      "Loop: claim_next_task; address swarmHealth.stuckSessions per the unstick rules; work the task; complete_task with a summary. Repeat until no open tasks or the budget says stop.",
    note: "Needs the NightSwarm MCP connector (OAuth) in this machine's coding agent — the worker reaches the backlog through the same gateway you do. Budgets compose: dispatch gates apply when the schedule fires, and budgetRemaining rides every claim so the loop winds down gracefully.",
  },
  {
    icon: I.send,
    t: "Chat-channel notifications",
    b: "Results land in your Discord/Slack, not just browser push — using a chat MCP that lives on the machine.",
    claudeMd: `# (add to any agent's CLAUDE.md)
## Notifications
When a job or schedule asks for a chat update, post it with
the discord MCP's reply tool, chat_id <your channel id>.
Outcome first, then 2-3 bullets. If the MCP isn't available
in this session, just finish — plane push still covers
needs-human moments.`,
    meta: ["pattern · any schedule", "mode · any"],
    prompt:
      "…do the work… When done, post a 5-line summary to Discord (discord MCP reply, chat_id <channel id>).",
    note: "The chat MCP and its credentials live on YOUR machine — the plane never holds them (same invariant as everything else). Works with any chat MCP the machine has; get a Discord channel id via right-click → Copy Channel ID. The plane's own Web Push still handles needs-human alerts independently.",
  },
] as const;

function Workflows() {
  return (
    <Section id="workflows">
      <h2>Recipes you can copy tonight.</h2>
      <p className="doc-lead">
        Each of these is the same three moves: a directory with a{" "}
        <code className="doc-inline-code">CLAUDE.md</code>, an agent pointed
        at it, a schedule. Adjust the file paths and the rhythm; keep the
        ground rules. The recipes are runtime-agnostic — point a Codex agent
        at the same directory (with the instructions in{" "}
        <code className="doc-inline-code">AGENTS.md</code> instead of{" "}
        <code className="doc-inline-code">CLAUDE.md</code>) and they work
        unchanged.
      </p>
      <Callout>
        <strong>Where do these CLAUDE.md files live?</strong> On the machine
        that runs the agent, inside the agent's working directory (e.g.{" "}
        <code className="doc-inline-code">~/agents/research/CLAUDE.md</code>,
        or <code className="doc-inline-code">AGENTS.md</code> for a Codex
        agent) — the coding agent reads it automatically every session. Three
        ways to
        put one there: <em>edit it from the dashboard</em> — open the agent
        and switch to its <strong>Instructions</strong> tab; the daemon
        writes the file and echoes back what landed on disk. Or create the
        folder and file on the machine yourself. Or — still a neat trick —{" "}
        <em>message the agent and ask it to write its own</em> ("create a
        CLAUDE.md in your working directory that says…"), since its session
        runs inside that folder with write access. For each recipe below:
        create the agent first, then paste the sketch into its Instructions
        tab (or send it as the first message).
      </Callout>
      <div className="doc-flows">
        {FLOWS.map((f) => (
          <article className="doc-flow" key={f.t}>
            <div className="head">
              <span className="ic">
                <Icon d={f.icon} size={16} />
              </span>
              <h3>{f.t}</h3>
            </div>
            <p>{f.b}</p>
            <Code title="CLAUDE.md sketch">{f.claudeMd}</Code>
            <div className="doc-flow-meta">
              {f.meta.map((m) => (
                <MonoChip tone="ghost" key={m}>
                  {m}
                </MonoChip>
              ))}
            </div>
            <Code title="schedule prompt">{f.prompt}</Code>
            {f.note && (
              <div className="doc-flow-note">
                <span className="ic">
                  <Icon d={I.zap} size={12} />
                </span>
                {f.note}
              </div>
            )}
          </article>
        ))}
      </div>
      <div className="doc-prose">
        <p>
          For each: create the agent with its working directory, put the{" "}
          <code>CLAUDE.md</code> in place (Instructions tab or on the
          machine), add a permission allowlist scoped to its output folders
          (section 03 — that one is edited on the machine), then add the
          schedule. Start in message mode and read the transcripts for a few
          mornings before granting anything wider — that's the autonomy
          ladder doing its job.
        </p>
      </div>
    </Section>
  );
}

/* ── 11 · Best practices ─────────────────────────────────────────── */

function BestPractices() {
  return (
    <Section id="best-practices">
      <h2>Lessons that keep a swarm healthy.</h2>
      <p className="doc-lead">
        Everything here was learned by running real agents in production —
        each practice prevents a specific way things go wrong.
      </p>

      <h3>Designing agents</h3>
      <ul className="doc-prose">
        <li>
          <strong>One responsibility per agent.</strong> A focused agent with
          a short CLAUDE.md outperforms a do-everything agent with a long
          one — and its sessions stay coherent for longer.
        </li>
        <li>
          <strong>Give every agent its own working directory</strong> and
          pre-approve its tools there. An agent that has to ask permission
          for its own core tools will eventually stall overnight.
        </li>
        <li>
          <strong>Warm-start your daily drivers.</strong> Agents you talk to
          often should have warm start enabled so the first message of the
          day isn't paying a cold-boot tax.
        </li>
      </ul>

      <h3>Writing scheduled prompts</h3>
      <ul className="doc-prose">
        <li>
          <strong>Make them idempotent.</strong> Write prompts that read the
          current state and only add what's missing ("find the last synced
          date, append newer rows"). Retries, machine wake-ups, and overlap
          with other automations then cost nothing.
        </li>
        <li>
          <strong>Be explicit about time.</strong> Tell the agent its
          timezone and how to compute dates — "today" is ambiguous at
          midnight and across machines.
        </li>
        <li>
          <strong>Results go to a system of record</strong> (notes, a repo,
          a tracker your machine has tools for); the session reply should be
          a summary that points at it.
        </li>
      </ul>

      <h3>Adopting safely</h3>
      <ul className="doc-prose">
        <li>
          <strong>Shadow-run anything that replaces an existing system.</strong>{" "}
          Run the new schedule in read-only/report-only mode alongside the
          old one until you trust its output. Cut over one pipeline at a
          time.
        </li>
        <li>
          <strong>Never give two automations the same artifact.</strong> Two
          writers on one file or inbox creates races no prompt can fix —
          offset their schedules or split the artifact.
        </li>
        <li>
          <strong>Climb the autonomy ladder.</strong> Read-only reports →
          drafts a human reviews → expanded scope, each step earned by a
          clean track record. Teams: keep the morning review a real ritual.
        </li>
      </ul>

      <h3>Reliability & cost</h3>
      <ul className="doc-prose">
        <li>
          <strong>Keep the night machine awake</strong> (Energy settings or
          <code> caffeinate</code>) — jobs queue politely while a machine
          sleeps, but your morning results arrive late.
        </li>
        <li>
          <strong>Watch the token widget</strong> before adding heavy
          schedules. Overnight work shares the same subscription capacity
          as your daytime usage. When you run both runtimes, usage and
          seat-limit state are tracked <em>per service</em> — Claude and
          Codex have separate buckets, so budget each seat on its own.
          Routing an <code>any</code> agent to the seat that still has
          headroom is how the swarm keeps moving when one limit hits.
        </li>
      </ul>

      <h3>Security</h3>
      <ul className="doc-prose">
        <li>
          <strong>Secrets never go in CLAUDE.md or prompts.</strong>{" "}
          Credentials belong in the machine-local MCP/tool configuration —
          they never leave the machine, and NightSwarm never sees them.
        </li>
        <li>
          <strong>Allowlist narrowly.</strong> Pre-approve only the tools an
          agent actually needs; everything else still asks.
        </li>
        <li>
          <strong>Transcripts contain what agents read.</strong> If an agent
          reads sensitive material, that context is in its session
          transcript — scope agents accordingly and rely on retention
          limits.
        </li>
      </ul>
      <Mech title="Where these came from">
        <p>
          Idempotent prompts came from parallel-running a migration; the
          allowlist rule from an agent burning its auto-answer budget on
          tool approvals; the two-writers rule from double-running a data
          sync; the awake-machine rule from a morning of "why is the queue
          full". The ladder is the operating philosophy for letting agents
          earn write access.
        </p>
      </Mech>
    </Section>
  );
}

/* ── 12 · Troubleshooting ────────────────────────────────────────── */

function Troubleshooting() {
  return (
    <Section id="troubleshooting">
      <h2>When something looks off.</h2>
      <p className="doc-lead">
        Most issues come down to one of four things: the daemon isn't
        running, a widget is showing old data, a session is waiting on a
        prompt, or you're looking for a transcript.
      </p>
      <div className="doc-trouble">
        <div className="doc-trouble-item">
          <div className="q">
            <span className="ic">
              <Icon d={I.zap} size={15} />
            </span>
            "authorization denied" reading Mail, Messages, or Safari data
          </div>
          <p>
            macOS protects those databases behind{" "}
            <strong>Full Disk Access</strong>, granted per app — and it
            can't be granted programmatically, by design. On that Mac,
            click the swarm icon → <strong>Grant Full Disk Access</strong>:
            it opens the System Settings pane with NightSwarm.app revealed
            — drag it in, and the daemon (which runs inside the app and
            inherits the grant) confirms with its own probe. On a headless
            machine running the daemon from the CLI, run{" "}
            <code className="doc-inline-code">nightswarmd setup-fda</code>{" "}
            instead — it sets up a dedicated daemon binary and walks the
            same grant-and-confirm loop. Machines without the grant show a
            warning on their machine card in the Swarm tab.
          </p>
        </div>
        <div className="doc-trouble-item">
          <div className="q">
            <span className="ic">
              <Icon d={I.cpu} size={15} />
            </span>
            A machine shows offline
          </div>
          <p>
            Check the menubar icon on that Mac. "Daemon running,
            disconnected" means it's reconnecting — give it a moment; the
            daemon retries automatically with backoff. "Daemon not running"
            means click <strong>Start daemon</strong> (or run{" "}
            <code className="doc-inline-code">nightswarmd run</code> in
            Terminal). Anything queued for that machine delivers the moment
            it comes back — schedules that fired while it slept aren't
            lost.
          </p>
        </div>
        <div className="doc-trouble-item">
          <div className="q">
            <span className="ic">
              <Icon d={I.refresh} size={15} />
            </span>
            A widget in Claude desktop shows stale data
          </div>
          <p>
            Widgets refresh themselves on load, but a long-lived
            conversation in the desktop app can pin an old rendering. Fully
            quit the Claude app (not just close the window) and start a new
            conversation — the widget will fetch fresh state. Most widgets
            also have a manual refresh button.
          </p>
        </div>
        <div className="doc-trouble-item">
          <div className="q">
            <span className="ic">
              <Icon d={I.bell} size={15} />
            </span>
            A session seems stuck
          </div>
          <p>
            Open the session from <strong>Activity</strong> (or the push
            notification). If it's waiting on a prompt, the approval bar
            shows the agent's screen and the options — answer from your
            phone. If it stalls on the same prompt nightly, add the
            permission to the agent's allowlist so it stops asking. A
            session that's truly wedged can be stopped from the same page
            and re-run from the queue.
          </p>
        </div>
        <div className="doc-trouble-item">
          <div className="q">
            <span className="ic">
              <Icon d={I.bell} size={15} />
            </span>
            A Codex session keeps asking for approval (or hit its limit)
          </div>
          <p>
            Codex's command-approval dialogs aren't auto-answered — they
            escalate to you on purpose, so a Codex agent that runs shell
            commands will push for approval more often than a Claude one.
            Pre-approving doesn't apply the same way; answer from the
            session page, or scope the agent to a read-only/file-only role so
            it asks less. If a session stalls with the Codex seat reporting a
            limit, that ChatGPT seat is out of capacity for the window —{" "}
            <code className="doc-inline-code">token_usage</code> and the budget
            card show the per-service split and when the limit resets. An{" "}
            <code className="doc-inline-code">any</code> agent will route
            around a limited seat to the other runtime on the same machine; a
            pinned <code className="doc-inline-code">codex</code> agent waits
            for the reset.
          </p>
        </div>
        <div className="doc-trouble-item">
          <div className="q">
            <span className="ic">
              <Icon d={I.list} size={15} />
            </span>
            Where transcripts live, and for how long
          </div>
          <p>
            Every session's transcript streams to the plane and stays
            readable from the session page — encrypted, visible only inside
            your tenant, and expiring automatically after 90 days. The
            machine itself keeps each runtime's original transcript files
            locally — Claude Code under{" "}
            <code className="doc-inline-code">~/.claude/projects</code>, Codex
            under <code className="doc-inline-code">~/.codex/sessions</code> —
            on each tool's own terms. The level of transcript and token
            detail differs between runtimes. Token usage per session, per
            agent, and per night is tracked on the dashboard and in the
            token-burn widget.
          </p>
        </div>
      </div>
    </Section>
  );
}

/* ── Footer ──────────────────────────────────────────────────────── */

function DocFooter() {
  return (
    <footer className="doc-footer">
      <div className="doc-container doc-footer-inner">
        <span className="doc-footer-copy">
          © 2026 nightswarm · docs grow with the beta
        </span>
        <nav className="doc-footer-links" aria-label="Footer">
          <Link to="/">Home</Link>
          <Link to="/dashboard">Dashboard</Link>
          <Link to="/privacy">Privacy</Link>
          <Link to="/terms">Terms</Link>
        </nav>
      </div>
    </footer>
  );
}

/* ── Page ────────────────────────────────────────────────────────── */

const SECTION_IDS = SECTIONS.map((s) => s.id);

export default function Docs() {
  const active = useActiveSection(SECTION_IDS);
  return (
    <div className="doc-page">
      <DocTopbar />
      <div className="doc-container doc-shell">
        <DocToc active={active} />
        <main className="doc-main">
          <header className="doc-hero">
            <span className="doc-hero-moon">
              <SwarmMoon size={40} animate />
            </span>
            <div className="doc-kicker">Documentation</div>
            <h1>Everything you need to hand work to the swarm.</h1>
            <p className="doc-lead">
              How NightSwarm fits together, how to enroll your first Mac,
              and how to shape agents you'd trust with the night shift. Each
              section starts in plain English; the mechanics fold out
              underneath when you want them.
            </p>
          </header>
          <Concepts />
          <GettingStarted />
          <ConfiguringAgents />
          <TalkingToAgents />
          <DispatchProfilesDoc />
          <Schedules />
          <NeedsYou />
          <Teams />
          <PrReviews />
          <Workflows />
          <BestPractices />
          <Troubleshooting />
        </main>
      </div>
      <DocFooter />
    </div>
  );
}
